/**
 * ADDRESS — Entita' polimorfica Liferay, attualmente usata solo da
 * Business nel CRM. Il design nested per-owner mantiene aperto il
 * supporto a futuri owner senza modifiche al frontend (basta aggiungere
 * un businessAddressApi-like analogo).
 *
 * Shape allineata al DTO REST Liferay: tutti i campi sono opzionali
 * perche' il backend non dichiara `required: [...]` in AddressInput.
 *
 * Campi read-only server-side (risolti da Liferay core):
 *   - country   (codice ISO2 derivato da countryId)
 *   - region    (nome derivato da regionId)
 *   - latitude/longitude (le colonne native di Liferay: nessun geocoder le calcola, e da FILO-52 non sono
 *     una posizione — COORDINATES: le coordinate di un'azienda stanno sul punto mappa dei suoi indirizzi)
 *
 * Campi `countryId`/`regionId` (int64, id del Country/Region Liferay)
 * sono ora accettati anche in input grazie ai 2 nuovi endpoint REST
 * `/v1_0/countries` e `/v1_0/country/{countryId}/regions` (vedi
 * entities/geography). UI: `CountrySelect` + `RegionSelect`.
 */

export interface Address {
  addressId: number;
  name?: string;
  description?: string;
  street?: string;
  city?: string;
  zip?: string;
  primary?: boolean;
  // Read-only server-side (resolved labels/coords)
  country?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  // Lookup ids Liferay
  countryId?: number;
  regionId?: number;
}

/**
 * Un campo dell'indirizzo come si mostra: il «-» che il core salva al posto di via, città e CAP mancanti
 * (FILO-52, `AddressRules` e `Constants.ADDRESS_DEFAULT_ZIP`, perché Liferay li pretende) è un campo vuoto.
 */
export const addressText = (value: string | null | undefined): string =>
  value?.trim() === '-' ? '' : value ?? '';

/** Payload per creazione: businessId viaggia via path, non nel body. */
export interface CreateAddressPayload {
  businessId: number;
  name?: string;
  description?: string;
  street?: string;
  city?: string;
  zip?: string;
  primary?: boolean;
  countryId?: number | null;
  regionId?: number | null;
}

/** Payload per update: businessId + addressId nel path. PATCH parziale. */
export interface UpdateAddressPayload {
  businessId: number;
  addressId: number;
  name?: string;
  description?: string;
  street?: string;
  city?: string;
  zip?: string;
  primary?: boolean;
  countryId?: number | null;
  regionId?: number | null;
}

export interface RemoveAddressPayload {
  businessId: number;
  addressId: number;
}
